import React from "react";
import { pageRegistry } from "../../App/pageRegistry";
import { PageId } from "../../Core/Content/Types";

const logo = "https://souledout-site.nyc3.digitaloceanspaces.com/so-trans.png";
const details: Record<
  PageId,
  { title: string; description: string; label: string }
> = {
  menu: {
    title: "What's on the menu?",
    description:
      "Fresh prices, favorite dishes, and something special for the season.",
    label: "Manage menu",
  },
  home: {
    title: "Make a first impression.",
    description: "Give guests a taste of what's waiting for them.",
    label: "Edit home page",
  },
  about: {
    title: "Tell your story.",
    description: "Share the people and the passion behind every plate.",
    label: "Edit about page",
  },
  navigation: {
    title: "Point the way.",
    description: "Manage your navigation links and the scrolling ordering notice.",
    label: "Manage navigation",
  },
  footer: {
    title: "Keep in touch.",
    description:
      "Keep your hours, contact details, and social links up to date.",
    label: "Update details",
  },
};
export function DashboardIconPiece({
  name,
  className = "",
}: {
  name: string;
  className?: string;
}) {
  const paths: Record<string, React.ReactNode> = {
    overview: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
    menu: (
      <>
        <path d="M5 3v6a3 3 0 0 0 6 0V3M8 3v18M19 21V3c-4 3-4 9 0 10" />
      </>
    ),
    home: (
      <>
        <path d="m3 10 9-7 9 7M5 9v12h14V9M9 21v-8h6v8" />
      </>
    ),
    about: (
      <>
        <path d="M4 4h6c2 0 2 2 2 2s0-2 2-2h6v16h-6c-2 0-2 1-2 1s0-1-2-1H4zM12 6v15" />
      </>
    ),
    navigation: (
      <>
        <path d="m21 3-7 18-3-8-8-3zM11 13l5-5" />
      </>
    ),
    footer: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="m3 7 9 6 9-6" />
      </>
    ),
    arrow: (
      <>
        <path d="M4 12h16m-6-6 6 6-6 6" />
      </>
    ),
    external: (
      <>
        <path d="M14 3h7v7m0-7L10 14M10 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5" />
      </>
    ),
    logout: (
      <>
        <path d="M9 4H4v16h5m5-12 4 4-4 4M8 12h10" />
      </>
    ),
    sparkle: (
      <>
        <path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z" />
      </>
    ),
  };
  return (
    <svg
      className={`cms-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.overview}
    </svg>
  );
}
export function DashboardSidebarPiece({
  active,
  onSignOut,
}: {
  active?: PageId;
  onSignOut?: () => void;
}) {
  return (
    <aside className="cms-sidebar">
      <a
        className="cms-logo"
        href="/dashboard"
        aria-label="SouledOut dashboard"
      >
        <img src={logo} alt="SouledOut Soul Food Cuisine" />
        <span>THE OWNER'S TABLE</span>
      </a>
      <div className="cms-nav-label">YOUR WEBSITE</div>
      <nav aria-label="Dashboard navigation">
        <a
          href="/dashboard"
          className={!active ? "is-active" : ""}
          aria-current={!active ? "page" : undefined}
        >
          <DashboardIconPiece name="overview" />
          Overview
        </a>
        {pageRegistry.map((page) => (
          <a
            key={page.id}
            href={page.editorPath}
            className={active === page.id ? "is-active" : ""}
            aria-current={active === page.id ? "page" : undefined}
          >
            <DashboardIconPiece name={page.id} />
            {page.id === "menu"
              ? "Menu & seasonal items"
              : page.id === "footer"
              ? "Contact & footer"
              : page.title}
          </a>
        ))}
      </nav>
      <div className="cms-sidebar-bottom">
        <div className="cms-sidebar-note">
          <DashboardIconPiece name="sparkle" />
          <p>
            A little update.
            <br />
            <strong>A fresh experience.</strong>
          </p>
        </div>
        <a href="/" target="_blank" rel="noreferrer">
          <DashboardIconPiece name="external" />
          View your website
        </a>
        {onSignOut && (
          <button onClick={onSignOut}>
            <DashboardIconPiece name="logout" />
            Sign out
          </button>
        )}
        <small>SouledOut · Made with soul.</small>
      </div>
    </aside>
  );
}
export function DashboardWelcomePiece() {
  return (
    <>
      <header className="cms-overview-heading">
        <div>
          <span className="cms-eyebrow">WELCOME TO YOUR DASHBOARD</span>
          <h1>Good things start here.</h1>
          <p>Your website, served just the way you like it.</p>
        </div>
        <a
          className="cms-website-link"
          href="/"
          target="_blank"
          rel="noreferrer"
        >
          View website <DashboardIconPiece name="external" />
        </a>
      </header>
      <section className="cms-hero" aria-label="Manage your menu">
        <div className="cms-hero-copy">
          <span className="cms-eyebrow">
            <span className="cms-gold-dot" /> FRESH FROM YOUR KITCHEN
          </span>
          <h2>
            Full of flavor.
            <br />
            <em>Always up to date.</em>
          </h2>
          <p>
            New specials, seasonal favorites, a fresh price.
            <br />
            Keep your guests coming back for more.
          </p>
          <a href="/dashboard/pages/menu" className="cms-hero-button">
            Make it yours <DashboardIconPiece name="arrow" />
          </a>
        </div>
        <div className="cms-hero-art" aria-hidden="true">
          <div className="cms-orbit" />
          <img
            src="https://souledout-site.nyc3.digitaloceanspaces.com/fried-chicken-tenders.png"
            alt=""
          />
          <div className="cms-hero-seal">
            GOOD FOOD.
            <br />
            <span>GOOD SOUL.</span>
          </div>
        </div>
      </section>
    </>
  );
}
export function DashboardPagesPiece() {
  return (
    <>
      <div className="cms-section-heading">
        <div>
          <span className="cms-eyebrow">A LITTLE CARE GOES A LONG WAY</span>
          <h2>What would you like to update?</h2>
        </div>
        <span className="cms-section-hint">Choose a place to start</span>
      </div>
      <div className="cms-grid">
        {pageRegistry.map((page, index) => (
          <a
            className={`cms-page-link cms-page-${page.id}`}
            key={page.id}
            href={page.editorPath}
          >
            <div className="cms-card-top">
              <span className="cms-card-icon">
                <DashboardIconPiece name={page.id} />
              </span>
              <span className="cms-card-number">0{index + 1}</span>
            </div>
            <span className="cms-card-category">{page.title}</span>
            <h3>{details[page.id].title}</h3>
            <p>{details[page.id].description}</p>
            <div className="cms-card-link">
              {details[page.id].label}
              <DashboardIconPiece name="arrow" />
            </div>
          </a>
        ))}
      </div>
      <section className="cms-workflow" aria-label="How editing works">
        <div>
          <span className="cms-eyebrow">YOU'RE IN CONTROL</span>
          <h3>A fresh look in three simple steps.</h3>
        </div>
        <ol>
          <li>
            <b>01</b>
            <span>
              Make it yours<small>Update your content.</small>
            </span>
          </li>
          <li>
            <b>02</b>
            <span>
              Take a look<small>Preview and save a draft.</small>
            </span>
          </li>
          <li>
            <b>03</b>
            <span>
              Serve it up<small>Publish when you're ready.</small>
            </span>
          </li>
        </ol>
      </section>
    </>
  );
}
export function LoginPiece({
  email,
  password,
  busy,
  onEmail,
  onPassword,
  onSubmit,
}: {
  email: string;
  password: string;
  busy: boolean;
  onEmail: (value: string) => void;
  onPassword: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="cms-login-layout">
      <div className="cms-login-intro">
        <span className="cms-eyebrow">THE OWNER'S TABLE</span>
        <h2>
          Your story.
          <br />
          Your flavor.
          <br />
          <em>Your website.</em>
        </h2>
        <p>
          A little space to keep everything
          <br />
          feeling like SouledOut.
        </p>
      </div>
      <form
        className="cms-card cms-login"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <span className="cms-eyebrow">WELCOME BACK</span>
        <h1>Pull up a seat.</h1>
        <p>Sign in to care for your corner of the web.</p>
        <label className="cms-field">
          Email
          <input
            autoComplete="username"
            type="email"
            required
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label className="cms-field">
          Password
          <input
            autoComplete="current-password"
            type="password"
            required
            value={password}
            onChange={(e) => onPassword(e.target.value)}
          />
        </label>
        <button className="cms-primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
          <DashboardIconPiece name="arrow" />
        </button>
        <small>Your next great update starts here.</small>
      </form>
    </div>
  );
}
